import errorLoggerService from "@/app/errors/errorLoggerService";
import environment from "@/environment";
import messages from "@/plugins/i18n";
import userStore from "@/store/modules/userStore";
import type { Address } from "@/components/controls/address/addresses";
import { dutySchemeType } from "@/components/pages/duty/composables/dutyConfiguration";

export function mapLoqateRetrieveToAddress(
  retrievedAddress: LoqateRetrieveResponse
): Address {
  const address: Address = {
    addressLine1: retrievedAddress.Line1,
    addressLine2: retrievedAddress.Line2,
    addressLine3: retrievedAddress.Line3,
    addressLine4: retrievedAddress.City,
    addressLine5: retrievedAddress.ProvinceName,
    addressLine6: retrievedAddress.CountryName,
    addressPostcode: retrievedAddress.PostalCode,
    addressBusinessName: retrievedAddress.Company, // Not sure what to bind this to
  };

  /**
   *  For the US:
   *  - addressLine3 = city
   *  - addressLine4 = US county
   *  - addressLine5 = state (province)
   *
   *  For everywhere else:
   *  - addressLine4 = city
   *  - addressLine5 = county (province)
   *
   *  The Loqate API does not appear to return a US county field, so
   *  leave addressLine4 blank.
   */
  if (dutySchemeType.value.isUs()) {
    address.addressLine2 = [retrievedAddress.Line2, retrievedAddress.Line3]
      .filter((line) => !!line)
      .join(", ");
    address.addressLine3 = retrievedAddress.City;
    address.addressLine4 = "";
  }

  return address;
}

// https://www.loqate.com/resources/support/apis/Capture/Interactive/Find/1.1/
type LoqateSearchParams = {
  Text: string; // Text to find
  IsMiddleware?: boolean; // Is called from middleware
  Container?: string; // Id from another search, used for refinement
  Origin?: string; // Starting location for search ISO 2/3 country character codes, csv, or IP address
  Countries?: string; // Limit location for search ISO 2/3 country character codes, csv
  Limit?: number; // Number of results
  Language?: string; // Language codes 2/4 char (en, en-gb)
  Bias?: boolean; // Used with origin, better narrows search to local places
  // Skipped filters for now
};

export type LoqateSearchResponse = {
  Id: string; // Id of result
  Type: "Address" | "Container"; // Address or group of addresses
  Text: string; // Display text
  Highlight: string; // Matching character groups
  Description: string; // Other info
};

// Options object as you can specify formats as additional params, omitted as not used
type LoqateRetrieveParams = {
  Id: string;
};

// https://www.loqate.com/resources/support/apis/Capture/Interactive/Retrieve/1.2/
export type LoqateRetrieveResponse = {
  Id: string;
  DomesticId: string;
  Language: string;
  LanguageAlternatives: string;
  Department: string;
  Company: string;
  SubBuilding: string;
  BuildingNumber: string;
  BuildingName: string;
  SecondaryStreet: string;
  Street: string;
  Block: string;
  Neighbourhood: string;
  District: string;
  City: string;
  Line1: string;
  Line2: string;
  Line3: string;
  Line4: string;
  Line5: string;
  AdminAreaName: string;
  AdminAreaCode: string;
  Province: string;
  ProvinceName: string;
  ProvinceCode: string;
  PostalCode: string;
  CountryName: string;
  CountryIso2: string;
  CountryIso3: string;
  CountryIsoNumber: string;
  SortingNumber1: string;
  SortingNumber2: string;
  Barcode: string;
  POBoxNumber: string;
  Label: string;
  Type: string;
  DataLevel: string;
};

export class LoqateService {
  private readonly findUrl =
    "https://api.addressy.com/Capture/Interactive/Find/v1.10/json3.ws" as const;

  private readonly retrieveUrl =
    "https://api.addressy.com/Capture/Interactive/Retrieve/v1.2/json3.ws" as const;

  private readonly key = environment.loqateApiKey;

  private applyDefaultSearchParams(
    params: LoqateSearchParams
  ): Required<LoqateSearchParams> {
    return {
      Text: params.Text,
      IsMiddleware: params.IsMiddleware ?? false,
      Container: params.Container ?? "",
      Origin: params.Origin ?? userStore.state.userLanguageLocale,
      Countries: params.Countries ?? "",
      Limit: params.Limit ?? 200,
      Language: params.Language ?? userStore.state.userLanguageLocale,
      Bias: params.Bias ?? true,
    };
  }

  public async searchForAddress(params: LoqateSearchParams) {
    const paramsWithDefaults = this.applyDefaultSearchParams(params);

    const searchParams = new URLSearchParams(
      Object.entries(paramsWithDefaults).map(([key, value]) => {
        return [key, value.toString()];
      })
    );

    searchParams.set("Key", this.key);

    const response = await fetch(this.findUrl, {
      method: "POST",
      headers: { Accept: "application/json" },
      mode: "cors",
      body: searchParams,
    });

    if (!response.ok) {
      errorLoggerService.logError(messages.smart_address_error_in_response);
      return;
    }

    const responseObject = await response.json();

    if (
      responseObject.Items.length == 1 &&
      responseObject.Items[0].Error !== undefined
    ) {
      errorLoggerService.logError(messages.smart_address_error_in_search);
      return;
    }

    return responseObject.Items as LoqateSearchResponse[];
  }

  public async resolveLoqateId(
    params: LoqateRetrieveParams
  ): Promise<Address | undefined> {
    const searchParams = new URLSearchParams(
      Object.entries(params).map(([key, value]) => {
        return [key, value.toString()];
      })
    );

    searchParams.set("Key", this.key);

    const response = await fetch(this.retrieveUrl, {
      method: "POST",
      headers: { Accept: "application/json" },
      mode: "cors",
      body: searchParams,
    });

    if (!response.ok) {
      errorLoggerService.logError(messages.smart_address_error_in_response);
      return;
    }

    const responseObject = await response.json();

    if (
      responseObject.Items.length === 1 &&
      responseObject.Items[0].Error !== undefined
    ) {
      errorLoggerService.logError(messages.smart_address_error_in_retrieve);
      return;
    }

    if (responseObject.Items === undefined) {
      errorLoggerService.logError(messages.smart_address_no_retrieve_item);
      return;
    }

    return mapLoqateRetrieveToAddress(
      responseObject.Items[0] as LoqateRetrieveResponse
    );
  }
}

export const loqateService = new LoqateService();
